import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { apiRequest, editorStore, useEditorStore } from './editorStore';
import './history.css';

export type VersionKind = 'publish' | 'auto' | 'manual' | 'restore';

export interface VersionSummary {
  id: string;
  versionNo: number;
  title: string;
  kind: VersionKind;
  name: string;
  published: boolean;
  createdAt: string;
}

const NAME_MAX = 60;

export function versionLabel(version: VersionSummary): string {
  if (version.name) return version.name;
  switch (version.kind) {
    case 'publish':
      return 'Shared version';
    case 'restore':
      return 'Before a restore';
    case 'manual':
      return 'Saved version';
    default:
      return 'Before you started editing';
  }
}

function dayLabel(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: days > 300 ? 'numeric' : undefined });
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** Groups versions (newest first) under day headings. */
export function groupByDay(versions: VersionSummary[], now = new Date()): Array<{ day: string; versions: VersionSummary[] }> {
  const groups: Array<{ day: string; versions: VersionSummary[] }> = [];
  for (const version of versions) {
    const day = dayLabel(version.createdAt, now);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.versions.push(version);
    else groups.push({ day, versions: [version] });
  }
  return groups;
}

interface HistoryDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function HistoryDialog({ open, onClose }: HistoryDialogProps) {
  const { siteId } = useEditorStore();
  const [versions, setVersions] = useState<VersionSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const response = await apiRequest<{ versions: VersionSummary[] }>(`/api/v1/sites/${siteId}/versions`);
      setVersions(response.versions);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load the history.');
    }
  }, [siteId]);

  useEffect(() => {
    if (!open) return;
    setVersions(null);
    setConfirmId(null);
    setNotice(null);
    setActionError(null);
    void load();
  }, [open, load]);

  const saveNamed = async (event: FormEvent) => {
    event.preventDefault();
    const label = name.trim();
    if (!label) return;
    setBusy('save');
    setActionError(null);
    setNotice(null);
    try {
      await editorStore.ensureSaved();
      await apiRequest(`/api/v1/sites/${siteId}/versions`, { method: 'POST', body: JSON.stringify({ name: label }) });
      setName('');
      setNotice(`Saved "${label}". You can come back to it any time.`);
      await load();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not save this version.');
    } finally {
      setBusy(null);
    }
  };

  const restore = async (version: VersionSummary) => {
    setBusy(version.id);
    setActionError(null);
    try {
      await editorStore.restoreVersion(version.id);
      onClose();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not restore this version.');
      setBusy(null);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Version history">
      <div className="history">
        <p className="history-intro">
          Go back to an earlier version of your page. A copy is kept each time you start editing and each time you share
          it. Restoring keeps what you have now in this list too, so nothing is lost.
        </p>

        <form className="history-save" onSubmit={saveNamed}>
          <label className="history-save-label" htmlFor="history-name">
            Save this version
          </label>
          <div className="history-save-row">
            <input
              id="history-name"
              className="sarnia-input"
              value={name}
              maxLength={NAME_MAX}
              placeholder="e.g. Before the summer menu"
              onChange={(event) => setName(event.target.value)}
            />
            <Button type="submit" variant="secondary" size="sm" loading={busy === 'save'} disabled={!name.trim() || busy !== null}>
              Save
            </Button>
          </div>
        </form>

        {notice ? (
          <p className="history-notice" role="status">
            <Icon name="check" size={15} /> {notice}
          </p>
        ) : null}
        {actionError ? (
          <p className="history-error" role="alert">
            <Icon name="alert" size={15} /> {actionError}
          </p>
        ) : null}

        <div className="history-current">
          <Icon name="edit" size={15} />
          <span>
            <strong>Your page now</strong>
            <span className="history-meta">What you see in the editor</span>
          </span>
        </div>

        {loadError ? (
          <div className="history-error" role="alert">
            {loadError}{' '}
            <button type="button" className="history-link" onClick={() => void load()}>
              Try again
            </button>
          </div>
        ) : versions === null ? (
          <p className="history-meta" aria-busy="true">
            Loading history…
          </p>
        ) : versions.length === 0 ? (
          <p className="history-empty">
            No earlier versions yet. The first one is kept as soon as you make a change.
          </p>
        ) : (
          groupByDay(versions).map((group) => (
            <section key={group.day} className="history-day" aria-label={group.day}>
              <h3 className="history-day-title">{group.day}</h3>
              <ol className="history-list">
                {group.versions.map((version) => (
                  <li key={version.id} className={`history-item history-item--${version.kind}`}>
                    <div className="history-item-row">
                      <span className="history-item-text">
                        <strong>{versionLabel(version)}</strong>
                        <span className="history-meta">
                          {timeLabel(version.createdAt)}
                          {version.published ? <span className="history-badge">Shared</span> : null}
                        </span>
                      </span>
                      {confirmId === version.id ? null : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={busy !== null}
                          onClick={() => setConfirmId(version.id)}
                          aria-label={`Restore ${versionLabel(version)} from ${group.day} ${timeLabel(version.createdAt)}`}
                        >
                          Restore
                        </Button>
                      )}
                    </div>
                    {confirmId === version.id ? (
                      <div className="history-confirm">
                        <p>Replace your page with this version? What you have now stays in the history.</p>
                        <div className="history-confirm-actions">
                          <Button type="button" size="sm" loading={busy === version.id} onClick={() => void restore(version)}>
                            Restore this version
                          </Button>
                          <Button type="button" size="sm" variant="ghost" disabled={busy !== null} onClick={() => setConfirmId(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ol>
            </section>
          ))
        )}
      </div>
    </Modal>
  );
}
